/**
 * Pure helpers for summarising the My Files grouping-endpoint responses.
 *
 * The /user-files/by-{project,target,date} endpoints return an array of groups,
 * each carrying total_files, observation_count and total_size (bytes). The three
 * group-list components all need the same roll-up for the My Files summary line.
 */

/**
 * Roll a list of file groups up into the summary stats shown above the list.
 *
 * @param {Array<{total_files?: number, observation_count?: number, total_size?: number}>} groups
 * @returns {{groups: number, files: number, observations: number, bytes: number}}
 */
/**
 * Sum file_size_bytes across a list of loaded file records. Used for nested
 * target/observation storage chips in the My Files view, where the actual file
 * objects (with file_size_bytes) are already loaded.
 *
 * @param {Array<{file_size_bytes?: number}>} files
 * @returns {number}
 */
export const sumFileBytes = (files) =>
  (Array.isArray(files) ? files : []).reduce((sum, f) => sum + (f.file_size_bytes || 0), 0);

/**
 * @param {Array} groups the response's `groups` array
 * @param {{observations?: number}} [totals] the response's own totals. `observations` MUST come from
 *   the server: an observation can straddle groups (two target names, or uploads crossing UTC
 *   midnight), so summing each group's observation_count double-counts it and made the three tabs
 *   report different observation counts for the same files. Files and bytes are safe to sum — a file
 *   belongs to exactly one target, one upload date and one project. Falls back to the old sum only
 *   when the server sends no total, so an older API keeps rendering something.
 */
export const computeGroupStats = (groups, totals = {}) => {
  const list = Array.isArray(groups) ? groups : [];
  return {
    groups: list.length,
    files: list.reduce((sum, g) => sum + (g.total_files || 0), 0),
    observations: typeof totals.observations === 'number'
      ? totals.observations
      : list.reduce((sum, g) => sum + (g.observation_count || 0), 0),
    bytes: list.reduce((sum, g) => sum + (g.total_size || 0), 0),
  };
};
